import { useEffect, useState } from "react"

import { useThrottle } from "shared/lib/performance/useThrottle"
import { useEvent } from "shared/lib/useEvent"

export interface ContainerDimensions {
  width: number // in rem
  height: number // in rem
  widthPx: number // in pixels
  heightPx: number // in pixels
}

// Helper function to convert pixels to rem
function pixelsToRem(pixels: number): number {
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  )
  return pixels / rootFontSize
}

export function useDimensions(
  element: HTMLElement | null
): ContainerDimensions {
  const [dimensions, setDimensions] = useState<ContainerDimensions>({
    width: 0,
    height: 0,
    widthPx: 0,
    heightPx: 0
  })

  const throttledSetDimensions = useThrottle(setDimensions, 100)

  const updateDimensions = useEvent((e: HTMLElement) => {
    const rect = e.getBoundingClientRect()
    const widthPx = rect.width
    const heightPx = rect.height

    throttledSetDimensions({
      width: pixelsToRem(widthPx),
      height: pixelsToRem(heightPx),
      widthPx,
      heightPx
    })
  })

  useEffect(() => {
    if (!element) return undefined

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver(entries => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        const widthRem = pixelsToRem(width)
        const heightRem = pixelsToRem(height)

        throttledSetDimensions({
          width: widthRem,
          height: heightRem,
          widthPx: width,
          heightPx: height
        })
      }
    })

    updateDimensions(element)

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [element, updateDimensions])

  return dimensions
}
